<template>
  <div class="line-items-container">
    <div class="line-items-header">
      <h3>{{ tableTitle }}</h3>
      <div class="line-items-actions">
        <button 
          v-if="!isEditMode && isManual" 
          @click="toggleEditMode" 
          class="btn btn-primary btn-edit-table"
          :title="$t('view_document.line_items.edit') || 'Edit this table'"
        >
          ✎ 
          <!-- {{ $t('view_document.line_items.edit') || 'Edit' }} -->
        </button>
        <template v-if="isEditMode">
          <button @click="cancelEdit" class="btn btn-secondary">
            X
            <!-- {{ $t('view_document.line_items.cancel') || 'Cancel' }} -->
          </button>
          <button 
            v-if="hasChanges" 
            @click="updateLineItems" 
            class="btn btn-success"
            :disabled="isSaving"
          >
            {{ isSaving ? ($t('view_document.line_items.updating') || 'Updating...') : ($t('view_document.line_items.update') || 'Update') }}
          </button>
        </template>
      </div>
    </div>

    <div v-if="!tableData.columns.length" class="no-data-message">
      {{ $t('view_document.line_items.no_data') || 'No line items found' }}
    </div>

    <div v-else class="table-wrapper">
      <table class="line-items-table">
        <thead>
          <tr>
            <th class="row-number">#</th>
            <th 
              v-for="column in tableData.columns" 
              :key="column.key"
              @click="highlightGeometry(getGeometryForHeader(column))"
              :class="{ 'is-editable': isEditMode, 'modified': editedHeaders[column.key] }"
              class="table-header"
            >
              <template v-if="isEditMode && editingHeaderKey === column.key">
                <input 
                  v-model="editedHeaders[column.key]" 
                  @blur="finishEditHeader(column.key)"
                  @keydown.enter="finishEditHeader(column.key)"
                  @keydown.escape="cancelEditHeader(column.key)"
                  :data-edit-key="`header_${column.key}`"
                  class="header-input"
                  autofocus
                />
              </template>
              <template v-else>
                <span 
                  @click="startEditHeader(column.key)"
                  class="header-text"
                >
                  {{ editedHeaders[column.key] !== undefined ? editedHeaders[column.key] : column.label }}
                </span>
              </template>
              <span class="confidence-badge">{{ getRoundedConfidence(column?.originalField?.LabelDetection?.Confidence || '-') }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, rowIndex) in tableData.rows" :key="rowIndex">
            <td class="row-number">{{ rowIndex + 1 }}</td>
            <td 
              v-for="column in tableData.columns" 
              :key="`${rowIndex}_${column.key}`"
              @click="highlightGeometry(row.cells[column.key]?.geometry)"
              :class="{ 
                'is-editable': isEditMode, 
                'modified': row.cells[column.key]?.isModified 
              }"
              class="table-cell"
            >
              <template v-if="isEditMode && editingCellKey === `${rowIndex}_${column.key}`">
                <textarea
                  v-model="row.cells[column.key].value" 
                  @blur="finishEditCell(row, column.key)"
                  @keydown.enter="finishEditCell(row, column.key)"
                  @keydown.escape="cancelEditCell(rowIndex, column.key)"
                  :data-edit-key="`${rowIndex}_${column.key}`"
                  class="cell-input"
                  autofocus
                />
              </template>
              <template v-else>
                <div 
                  @click="startEditCell(rowIndex, column.key)"
                  class="cell-content"
                >
                  <span class="cell-value">{{ row.cells[column.key]?.value }}</span>
                  <span class="confidence-badge">{{ getRoundedConfidence(row.cells[column.key]?.confidence || '-') }}</span>
                </div>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { convertTableToLineItems, getGeometryForHeader } from '@/utils/lineItemsHelper'
import { nextTick } from 'vue'
import { get, put, post } from '@/utils/api.js'
export default {
  name: 'LineItemsTable',
  props: {
    tableData: {
      type: Object,
      required: true,
      // Expected structure: { columns, rows, originalData, groupIndex }
    },
    allTableData: {
      type: Array,
      required: true,
      // All table data so we can send complete structure back
    },
    resultDocumentId: {
      type: Number,
      required: true,
    },
    isManual:{
      type: Boolean,
      default: false
    }
  },
  computed: {
    tableTitle() {
      const baseTitle = this.$t('view_document.line_items.title') || 'Line Items';
      console.log("this.tableData===>", this.tableData);
      if (this.tableData && this.tableData.groupIndex !== undefined) {
        return `Table ${this.tableData.groupIndex + 1}`;
      }
      return baseTitle;
    },
  },
  data() {
    return {
      isEditMode: false,
      isSaving: false,
      editingCellKey: null,
      editingHeaderKey: null,
      editedHeaders: {},
      originalHeaders: {},
      originalCellValues: {}, // Track original cell values
      hasChanges: false,
    };
  },
  watch: {
    tableData: {
      handler(newVal) {
        console.log('[LineItemsTable] Watch - tableData changed:', newVal);
        // Only reset edit state if NOT in edit mode
        if (newVal && !this.isEditMode) {
          this.resetEditState();
        }
      },
      deep: false,
      immediate: true,
    },
  },
  mounted() {
    this.resetEditState();
    console.log('[LineItemsTable] Mounted with tableData:', this.tableData);
  },
  methods: {
    toggleEditMode() {
      this.isEditMode = true;
      this.resetEditState();
    },
    cancelEdit() {
      this.isEditMode = false;
      this.resetEditState();
    },
    resetEditState() {
      this.editedHeaders = {};
      this.originalHeaders = {};
      this.originalCellValues = {};
      this.editingCellKey = null;
      this.editingHeaderKey = null;
      this.hasChanges = false;
      
      // Initialize headers from current table data
      if (this.tableData && this.tableData.columns) {
        this.tableData.columns.forEach((column) => {
          this.originalHeaders[column.key] = column.label;
        });
      }
      
      // Initialize original cell values
      if (this.tableData && this.tableData.rows) {
        this.tableData.rows.forEach((row, rowIndex) => {
          Object.keys(row.cells).forEach((cellKey) => {
            const key = `${rowIndex}_${cellKey}`;
            this.originalCellValues[key] = row.cells[cellKey].value;
          });
        });
      }
    },
    startEditHeader(columnKey) {
      if (!this.isEditMode) return;
      this.editingHeaderKey = columnKey;
      if (!this.editedHeaders[columnKey]) {
        this.editedHeaders[columnKey] = this.originalHeaders[columnKey];
      }
    },
    finishEditHeader(columnKey) {
      if (this.editedHeaders[columnKey] !== this.originalHeaders[columnKey]) {
        this.hasChanges = true;
      }
      this.editingHeaderKey = null;
    },
    cancelEditHeader(columnKey) {
      delete this.editedHeaders[columnKey];
      this.editingHeaderKey = null;
    },
    startEditCell(rowIndex, columnKey) {
      if (!this.isEditMode) return;
      this.editingCellKey = `${rowIndex}_${columnKey}`;
      // Use nextTick to ensure input is rendered before focusing
      nextTick(() => {
        const input = document.querySelector(`input[data-edit-key="${this.editingCellKey}"]`);
        if (input) input.focus();
      });
    },
    finishEditCell(row, columnKey) {
      const rowIndex = row.rowIndex;
      const key = `${rowIndex}_${columnKey}`;
      const originalValue = this.originalCellValues[key];
      const newValue = row.cells[columnKey].value;
      
      // Mark as modified if value changed
      if (newValue !== originalValue) {
        row.cells[columnKey].isModified = true;
        this.hasChanges = true;
      }
      
      this.editingCellKey = null;
    },
    cancelEditCell(rowIndex, columnKey) {
      this.editingCellKey = null;
    },
    getGeometryForHeader(column) {
      return getGeometryForHeader(column, this.tableData.rows);
    },
    highlightGeometry(geometry) {
      this.$emit('highlight-geometry', geometry);
    },
    async updateLineItems() {
      try {
        this.isSaving = true;
        console.log('[LineItemsTable] updateLineItems called for table', this.tableData.groupIndex);
        
        // Create a copy of all tables with the current one updated
        const allUpdatedTables = this.allTableData.map((table) => {
          if (table.groupIndex === this.tableData.groupIndex) {
            // This is the table being edited - convert it back with changes
            const converted = convertTableToLineItems(table, this.editedHeaders);
            // convertTableToLineItems returns an array, get the first element
            return Array.isArray(converted) ? converted[0] : converted;
          }
          // Other tables unchanged
          return table.originalData;
        });

        console.log('[LineItemsTable] Sending all tables back:', allUpdatedTables);

        // Send all tables to API
        const response = await put(`/v1/line-items?mx_job_result_document_id=${this.resultDocumentId}&mx_job_id=${this.$route.query.jobId}`, {
          line_item_data: allUpdatedTables,
        });

        console.log('[LineItemsTable] API Response:', response.data);
        this.$emit('updated', response.data);
        this.isEditMode = false;
        this.resetEditState();
        this.$emit('success', 'Line items updated successfully');
      } catch (error) {
        console.error('[LineItemsTable] Error updating line items:', error);
        this.$emit('error', 'Failed to update line items: ' + error.message);
      } finally {
        this.isSaving = false;
      }
    },
     getRoundedConfidence(confidence) {
      if (typeof confidence !== 'number') {
        return '-';
      }
     return Number(confidence.toFixed(2));
    }
  },
};
</script>

<style scoped>
.line-items-container {
  background: white;
  border-radius: 8px;
  margin-bottom: 20px;
}

.line-items-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 5px;
  padding-top: 10px;
  border-bottom: 1px solid #e0e0e0;
}

.line-items-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.line-items-actions {
  display: flex;
  gap: 10px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-primary {
  background-color: var(--primary-color);
  color: white;
}

.btn-primary:hover {
  opacity: 0.9;
}

.btn-edit-table {
  padding: 8px 14px;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.btn-secondary {
  background-color: #e0e0e0;
  color: #333;
}

.btn-secondary:hover {
  background-color: #d0d0d0;
}

.btn-success {
  background-color: #4caf50;
  color: white;
}

.btn-success:hover:not(:disabled) {
  opacity: 0.9;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.no-data-message {
  padding: 20px;
  text-align: center;
  color: #999;
  font-style: italic;
}

.table-wrapper {
  overflow-x: auto;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  max-height: 500px;
  background-color: white;
  -webkit-overflow-scrolling: touch;
}

.line-items-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  table-layout: auto;
}

.line-items-table thead {
  background-color: #f5f5f5;
  position: sticky;
  top: 0;
  z-index: 10;
}

.line-items-table th,
.line-items-table td {
  padding: 10px 8px;
  border: 1px solid #e0e0e0;
  text-align: left;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  max-width: 200px;
  vertical-align: top;
}

.row-number {
  width: 50px;
  text-align: center;
  font-weight: 600;
  background-color: #f9f9f9;
}

.table-header,
.table-cell {
  position: relative;
}

.table-header.is-editable,
.table-cell.is-editable {
  cursor: pointer;
}

.table-header:hover.is-editable,
.table-cell:hover.is-editable {
  background-color: #f0f0f0;
}

.table-header.modified,
.table-cell.modified {
  background-color: #fff9e6;
  border-left: 3px solid #ffc107;
}

.header-text,
.cell-content {
  display: contents;
}

.header-input,
.cell-input {
  width: 100%;
  padding: 8px 10px;
  border: 2px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  font-family: inherit;
  font-weight: 400;
  color: #333;
  background-color: #fafafa;
  transition: all 0.2s ease;
  line-height: 1.4;
  min-height: 34px;
  box-sizing: border-box;
  resize: none;
}

.header-input:focus,
.cell-input:focus {
  outline: none;
  border-color: var(--primary-color);
  background-color: #fff;
  box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb), 0.1);
}

.cell-value {
  display: flex;
  align-items: center;
  word-break: break-word;
  font-size: 13px;
  line-height: 1.4;
  min-height: 34px;
}

.confidence-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  background-color: #e3f2fd;
  color: #1976d2;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
  margin-right: 4px;
  min-width: fit-content;
}

.table-header .confidence-badge {
  font-size: 9px;
}
</style>
